import { DiscordSDK } from '@discord/embedded-app-sdk'
import { log } from './log'
import { PROXY_PREFIX, postJson } from './net'
import { setupPlatform, type PlatformInfo } from './platform'

const CLIENT_ID = import.meta.env.VITE_DISCORD_CLIENT_ID

// Deze scopes moeten in het Developer Portal ook aangevinkt staan, anders
// weigert authorize().
const SCOPES = ['identify', 'guilds', 'applications.commands'] as const

export interface DiscordUser {
  id: string
  username: string
  global_name: string | null
  avatar: string | null
}

export interface DiscordSession {
  sdk: DiscordSDK
  accessToken: string
  user: DiscordUser
  /** Uniek per launch in een voicekanaal. Iedereen die dezelfde activity opent
   *  deelt deze waarde, dus dit is onze room-sleutel. */
  instanceId: string
  channelId: string | null
  guildId: string | null
  platform: PlatformInfo
}

/** Discord geeft de iframe een frame_id mee. Zonder die parameter draaien we
 *  gewoon in een los browsertabblad en heeft de SDK niets om mee te praten. */
export function isInsideDiscord(): boolean {
  return new URLSearchParams(location.search).has('frame_id')
}

export function hasClientId(): boolean {
  return CLIENT_ID.trim().length > 0 && !CLIENT_ID.startsWith('vul-hier')
}

/**
 * De volledige handshake:
 *   1. SDK opzetten en wachten tot Discord klaar is (ready).
 *   2. authorize() vragen om een OAuth code.
 *   3. Die code door de backend laten omwisselen voor een access_token,
 *      want daar is het client secret voor nodig en dat hoort niet in de
 *      browser thuis.
 *   4. authenticate() met dat token, waarna de SDK weet wie er speelt.
 */
export type StepReporter = (key: string) => void

export async function connectToDiscord(onStep: StepReporter = () => {}): Promise<DiscordSession> {
  if (!hasClientId()) {
    throw new Error('DISCORD_CLIENT_ID ontbreekt in .env, de frontend weet niet welke app dit is')
  }

  const sdk = new DiscordSDK(CLIENT_ID)

  onStep('loading.sdk')
  log('info', 'Wachten op discordSdk.ready()')
  await sdk.ready()
  log('ok', `SDK klaar, instanceId ${sdk.instanceId}`)

  log('info', 'authorize() aanvragen')
  const { code } = await sdk.commands.authorize({
    client_id: CLIENT_ID,
    response_type: 'code',
    state: '',
    prompt: 'none',
    scope: [...SCOPES],
  })
  log('ok', 'OAuth code ontvangen')

  onStep('loading.auth')
  log('info', 'Code omwisselen bij de backend')
  const { access_token: accessToken } = await postJson<{ access_token: string }>('/token', { code })
  log('ok', 'Access token ontvangen van de backend')

  log('info', 'authenticate() met het access token')
  const auth = await sdk.commands.authenticate({ access_token: accessToken })
  if (!auth) {
    throw new Error('authenticate() gaf niets terug')
  }
  log('ok', `Ingelogd als ${auth.user.global_name ?? auth.user.username}`)

  // Pas hier, want de meeste events vragen om een geauthenticeerde verbinding.
  const platform = await setupPlatform(sdk)

  return {
    sdk,
    accessToken,
    user: {
      id: auth.user.id,
      username: auth.user.username,
      global_name: auth.user.global_name ?? null,
      avatar: auth.user.avatar ?? null,
    },
    instanceId: sdk.instanceId,
    channelId: sdk.channelId,
    guildId: sdk.guildId,
    platform,
  }
}

export function displayName(user: DiscordUser): string {
  return user.global_name ?? user.username
}

/**
 * Ook plaatjes vallen onder de CSP: cdn.discordapp.com is rechtstreeks
 * geblokkeerd. Dit vraagt om een URL Mapping in het Developer Portal met
 * prefix /cdn naar cdn.discordapp.com.
 */
export function avatarUrl(userId: string, avatarHash: string | null): string {
  if (avatarHash) {
    return `${PROXY_PREFIX}/cdn/avatars/${userId}/${avatarHash}.png?size=64`
  }
  const index = (BigInt(userId) >> 22n) % 6n
  return `${PROXY_PREFIX}/cdn/embed/avatars/${index}.png`
}
